import { fetchTimeline } from '../../../../services/timeline';
import type { TimelineEvent } from '../../../patient-files/actions/types';
import { sortEventsLatestFirst, TIMELINE_VERSION_FETCH_LIMIT } from './timelineVersionUtils';
import type { DocumentVersion } from './VersionedPdfPane';

// SDI and RDI timeline payloads share one shape: files.{filesKey}.patientFileId
// for the rendered PDF, formData.patientName/formData.date for the filename -
// mirrors the dashboard's sdi.ts/rdi.ts (buildClinicalFilename).
export interface ClinicalDocConfig {
    timelineType: string;
    filesKey: string;
    namePrefix: string;
}

export const SDI_VERSION_CONFIG: ClinicalDocConfig = {
    timelineType: 'sdi_submission',
    filesKey: 'sdiPdf',
    namePrefix: 'SDI'
};

export const RDI_VERSION_CONFIG: ClinicalDocConfig = {
    timelineType: 'rdi_submission',
    filesKey: 'rdiPdf',
    namePrefix: 'RDI'
};

function extractPatientFileId(event: TimelineEvent, filesKey: string): string | null {
    const files = event.payload?.files as Record<string, unknown> | undefined;
    const doc = files?.[filesKey] as Record<string, unknown> | undefined;
    const id = doc?.patientFileId;
    return typeof id === 'string' ? id : null;
}

function buildVersionFileName(event: TimelineEvent, namePrefix: string): string {
    const formData = event.payload?.formData as Record<string, unknown> | undefined;
    const rawName = formData?.patientName;
    const rawDate = formData?.date;
    const name = typeof rawName === 'string' ? rawName : '';
    const date = typeof rawDate === 'string' ? rawDate : '';
    const sanitized = name
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^\w.-]/g, '');
    const versionSuffix = event.version > 1 ? `-v${event.version}` : '';
    return `${namePrefix}-${sanitized}-${date}${versionSuffix}.pdf`;
}

export interface ClinicalDocVersionsResult {
    versions: DocumentVersion[];
    /** True when the raw fetch hit TIMELINE_VERSION_FETCH_LIMIT - older versions may be hidden. */
    hasMoreVersions: boolean;
}

export async function fetchClinicalDocVersions(
    patientId: string,
    config: ClinicalDocConfig
): Promise<ClinicalDocVersionsResult> {
    const { data } = await fetchTimeline(patientId, {
        type: config.timelineType,
        limit: TIMELINE_VERSION_FETCH_LIMIT
    });
    const active = sortEventsLatestFirst(data.filter((e) => e.status === 'active'));
    const versions = active
        .map((event) => {
            const patientFileId = extractPatientFileId(event, config.filesKey);
            if (!patientFileId) return null;
            return {
                id: event.id,
                version: event.version,
                createdAt: event.createdAt,
                patientFileId,
                fileName: buildVersionFileName(event, config.namePrefix)
            };
        })
        .filter((v): v is DocumentVersion => v !== null);
    return { versions, hasMoreVersions: data.length >= TIMELINE_VERSION_FETCH_LIMIT };
}
